import { z } from "zod";
/**
 * The athlete profile on the wire — `GET`/`PUT /me/profile` (`D114`).
 *
 * ── WHY THE APP WRITES THIS AND NOT THE AGENT ───────────────────────────────
 * The onboarding asks these three facts through the chat, but the answer never
 * travels through it: the person taps, the app `PUT`s here with
 * `actorPath: "direct"`, and only then sends the resolved label as a message
 * (`D114`). So the actor of a profile row is the person, literally, and the
 * change feed can say so — which is why `profile` is a `CHANGE_ENTITIES`
 * member (`change-log.ts`) and why `write-tools.ts:56-67`'s withheld
 * `updateProfile` can stop being withheld.
 *
 * The same route serves the settings screen later; there is no second shape.
 */
/**
 * `YYYY-MM-DD`, the wire form for a day rather than an instant.
 *
 * A date of birth is a DAY. Sent as an ISO instant it acquires a timezone, and
 * a birthday stored at `2000-03-01T00:00:00Z` is 28 February for every person
 * west of Greenwich — an age off by one on the boundary, computed silently.
 *
 * Declared here and imported by the other training schemas rather than
 * restated: `agent/data-access.ts` keeps its own private copy for the service
 * binding, and two copies of `z.iso.date()` is already one more than anybody
 * needs to read.
 */
export const calendarDay = z.iso.date();
/**
 * `other` is the STORED word and the label is *"Prefer not to say"* (`D115`).
 * The value is not renamed to match the label: the column already holds
 * `other` (`schema.ts:791-800`, `ATHLETE_SEXES`), and a wire value that
 * disagrees with the column is a mapping nobody can see is wrong.
 */
export const ATHLETE_SEXES = ["female", "male", "other"];
export const athleteSex = z.enum(ATHLETE_SEXES);
/**
 * The bound on the free-text motivation (`D129`, blueprint item 22).
 *
 * The question is *"What brought you here today? One sentence is enough."*, so
 * the bound is a sentence with room to breathe rather than an essay. 280 is
 * chosen and not inherited: this is the ONE Session 1 answer with no enum
 * behind it, which makes the length the whole of its validation. It is the same
 * number as `MAX_MOTIVATION_LENGTH` in `@repo/training`'s aggregate, restated
 * here because the module may not import the contract and the contract may not
 * import the module — the pair is pinned by `@repo/training`'s
 * `src/__tests__/contract-vocabulary.test.ts`, which is where every other
 * restated-on-both-sides value is held equal.
 */
export const MAX_MOTIVATION_LENGTH = 280;
/**
 * The four lifestyle vocabularies (`D148`), collected by the "improve your
 * plan" cards on home and editable in settings — never by the intake.
 *
 * Every one is restated in `@repo/training` and held equal by its
 * `contract-vocabulary.test.ts`, like `ATHLETE_SEXES` above. Two of the seven
 * facts move a number (`heavy` occupation and short or poor habitual sleep
 * each cut planned volume by ten per cent); the other five are read by the
 * trainer and change nothing.
 */
export const OCCUPATIONS = [
    "sedentary",
    "standing",
    "active",
    "heavy",
];
export const occupation = z.enum(OCCUPATIONS);
/** Protein-containing meals on an ordinary day. */
export const PROTEIN_MEALS = [
    "zero_to_one",
    "two",
    "three",
    "four_plus",
];
export const proteinMeals = z.enum(PROTEIN_MEALS);
export const PROTEIN_SUPPLEMENTS = ["regular", "sometimes", "no"];
export const proteinSupplement = z.enum(PROTEIN_SUPPLEMENTS);
/** What the person has done before — several may apply, or `none`. */
export const PREVIOUS_MODALITIES = [
    "weights",
    "crossfit",
    "powerlifting",
    "bodybuilding",
    "calisthenics",
    "endurance",
    "team_sport",
    "martial_arts",
    "other",
    "none",
];
export const previousModality = z.enum(PREVIOUS_MODALITIES);
/**
 * "Como você nos conheceu?" — the intake's optional last question (`D242`),
 * one choice. Restated in `@repo/training` and held equal by its
 * `contract-vocabulary.test.ts`, like `ATHLETE_SEXES`.
 *
 * `trainer_gym` is "meu personal ou academia" and `ai_assistant` is
 * "ChatGPT / IA". The values are stored and grouped on in analytics forever
 * (`D238`), so a value is never renamed — a new channel is a new value.
 */
export const HOW_HEARD_CHOICES = [
    "instagram",
    "tiktok",
    "youtube",
    "friend_family",
    "trainer_gym",
    "google",
    "ai_assistant",
    "play_store",
    "other",
];
export const howHeardChoice = z.enum(HOW_HEARD_CHOICES);
/**
 * The bound on "other"'s free text (`D242`). A channel's name, not a
 * sentence — "um podcast", "o grupo da corrida" — so shorter than the
 * motivation's 280. Restated in `@repo/training` beside `MAX_MOTIVATION_LENGTH`
 * and pinned by the same vocabulary test.
 */
export const MAX_HOW_HEARD_OTHER_LENGTH = 120;
/** Habitual hours, in half-hour steps. The bounds are the card's own. */
const sleepHours = z.number().min(4).max(10).multipleOf(0.5);
const profileShape = {
    dateOfBirth: calendarDay,
    /**
     * Nullable and not optional: *"I would rather not say"* is a real answer to
     * the height question and it has to be distinguishable from *"the client
     * forgot the field"*, which `z.strictObject` would otherwise accept as the
     * same thing. The bounds are the ones the onboarding's number step offers
     * (`wire-contracts.md` §4).
     */
    heightCm: z.int().min(100).max(250).nullable(),
    /**
     * The intake's LAST question, and the one that may be skipped (`D129`).
     *
     * ── OPTIONAL **AND** NULLABLE, WHICH `heightCm` DELIBERATELY IS NOT ───────
     * Height distinguishes *"I would rather not say"* from *"the client forgot
     * the field"* because those are different answers. Here they are the SAME
     * answer: `D129` makes the question skippable and requires that empty and
     * absent be indistinguishable downstream, so `undefined`, `null` and `""`
     * all mean *no sentence*. Optional is also what lets a client written before
     * this field existed keep `PUT`ting a valid profile — it omits the key, and
     * the server leaves whatever is stored rather than wiping it. Clearing an
     * answer stays expressible, as an explicit `null`.
     *
     * ── IT IS TONE AND HORIZON, NEVER A PRESCRIPTION (`D19`) ─────────────────
     * Nothing in a training plan may be justified by this text and it carries no
     * evidence identifier. It tells the trainer how to speak and whether there
     * is a date on the calendar.
     */
    motivation: z.string().max(MAX_MOTIVATION_LENGTH).nullable().optional(),
    /**
     * "Como você nos conheceu?" (`D242`) — optional AND nullable on the
     * `motivation` precedent: omitted leaves what is stored (every settings
     * write, every client older than the field), `null` is "skipped". Only the
     * intake sends it.
     */
    howHeard: howHeardChoice.nullable().optional(),
    /**
     * The free text beside `other`, and only beside it: the server stores it
     * when the choice is `other` and clears it otherwise, so a later change of
     * choice cannot leave a stale sentence under a different channel.
     */
    howHeardOther: z
        .string()
        .max(MAX_HOW_HEARD_OTHER_LENGTH)
        .nullable()
        .optional(),
    /**
     * The seven lifestyle facts (`D148`), every one optional AND nullable on
     * the `motivation` precedent: omitted leaves what is stored, `null` clears
     * it. They are asked one card at a time, long after the intake, so a `PUT`
     * from the settings screen must be able to carry six of them untouched.
     */
    occupation: occupation.nullable().optional(),
    previousModalities: z.array(previousModality).max(10).nullable().optional(),
    proteinMeals: proteinMeals.nullable().optional(),
    proteinSupplement: proteinSupplement.nullable().optional(),
    sex: athleteSex,
    sleepHoursWeekday: sleepHours.nullable().optional(),
    sleepHoursWeekend: sleepHours.nullable().optional(),
    /** 1 (poor) to 5 (good) — habitual, not last night's; that is `readiness`. */
    sleepQuality: z.int().min(1).max(5).nullable().optional(),
};
export const profileInput = z.strictObject(profileShape);
/**
 * The stored profile. `z.object` and not `z.strictObject`: a response the
 * server later widens must not fail the parse in an app already installed.
 *
 * `motivation` keeps the input's `optional()` here rather than being tightened
 * to a required `nullable()`, and that is the same argument running the other
 * way. The server always sends it, so a required key would parse fine today —
 * but the APK and the Worker ship on separate clocks, and a phone updated
 * before the API deploys would then fail the WHOLE profile parse against a
 * server whose `athlete_profile` predates `0008`. That failure would land on
 * the settings screen as a missing profile, which is not what happened.
 * `undefined` and `null` are the same answer here for the same reason they are
 * on the way in (`D129`), so tolerating the absence costs no information.
 */
export const profileOutput = z.object({
    ...profileShape,
    updatedAt: z.iso.datetime(),
});
//# sourceMappingURL=profile.js.map